import {
  COMPOSITE_DEFINITIONS,
  QUESTION_DEFINITIONS,
  getCompositeSectionDescriptors,
  type AdvisoryQuestionId,
} from '../index';
import type { AdvisoryBriefDocumentInputV01 } from './types';

/**
 * The reason stamped on an omission this helper adds. Says what happened, so a
 * board patched here is never mistaken for one the backend produced this way.
 */
export const DECLARED_AFTER_CAPTURE =
  'Section declared on this board after the answer was captured (GL-DEV-813)';

/**
 * Account for sections the board gained after a captured answer was written.
 *
 * The v0.1 contract requires every section a board declares to appear in
 * `sections` or `omitted` — a slot that is neither filled nor explained is a hole
 * the client can neither draw nor caption. GL-DEV-813 added `metric-strip` and
 * `evidence-table` as optional sections to 21 of the 24 boards, so that computed
 * figures would have somewhere to go. Answers captured before that change mention
 * them nowhere, and now fail validation with `component.unaccounted`.
 *
 * **The captures are not edited.** `developmentFirestore*Sample.v0.1.json` are
 * records of what the backend actually wrote, down to the per-document
 * `updateTime`; adding sections to those files would make them a record of
 * nothing. This patches a COPY at read time, and only ever by adding an omission
 * for a section the capture does not mention — it never touches a section the
 * backend filled, and never changes one it explained.
 *
 * Temporary. Once these fixtures are re-captured from a run against the current
 * catalogue, every declared section is accounted for by the backend and this
 * returns its input unchanged.
 */
export function accountForSectionsDeclaredSinceCapture(
  rendering: unknown,
  questionId: AdvisoryQuestionId
): unknown {
  if (rendering === null || typeof rendering !== 'object' || Array.isArray(rendering)) {
    return rendering;
  }
  const board = rendering as Record<string, unknown>;
  const question = QUESTION_DEFINITIONS[questionId];
  const composite = question ? COMPOSITE_DEFINITIONS[question.compositeId] : undefined;
  if (!composite) return rendering;

  const sections = Array.isArray(board.sections) ? board.sections : [];
  const omitted = Array.isArray(board.omitted) ? board.omitted : [];
  const mentioned = new Set(
    [...sections, ...omitted]
      .map(entry =>
        entry !== null && typeof entry === 'object'
          ? (entry as Record<string, unknown>).componentId
          : undefined
      )
      .filter((componentId): componentId is string => typeof componentId === 'string')
  );

  const added = getCompositeSectionDescriptors(composite)
    .filter(descriptor => !mentioned.has(descriptor.componentId))
    .map(descriptor => ({
      componentId: descriptor.componentId,
      title: descriptor.title,
      role: descriptor.role,
      reason: DECLARED_AFTER_CAPTURE,
    }));
  if (added.length === 0) return rendering;

  return { ...board, omitted: [...omitted, ...added] };
}

/**
 * The same patch applied to every answer of a captured document.
 *
 * Applied where the fixture is exported rather than in each test, so the demo
 * pages that render these captures see exactly what the tests validate.
 */
export function accountForCapturedAnswers(
  document: AdvisoryBriefDocumentInputV01
): AdvisoryBriefDocumentInputV01 {
  return {
    ...document,
    answers: document.answers.map(answer => ({
      ...answer,
      rendering: accountForSectionsDeclaredSinceCapture(
        answer.rendering,
        answer.questionId as AdvisoryQuestionId
      ),
    })),
  };
}
